import { QuickAddProductFormData } from "@/formTypes/QuickAddProductDataType";
import styles from "./style.module.scss";
import cx from "classnames";
import { useRef, useState } from "react";
import { Button, Col, FormGroup, Input, InputGroup, InputGroupText, Label, Row } from "reactstrap";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { ProductApi, ProductDataType } from "@/api/ProductsApi";
import { NotFoundError } from "@/api/ApiClient";
import { showAlert } from "@utils/Alert";
import LoadingScreen from "@components/content/LoadingScreen";
import { calculateProductPrice } from "@utils/EDocument";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { addProduct } from "@/store/slices/eDocumentSlides";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { TAXES_PERCENTAGES_VALUES } from "@utils/Constants";
import { goNextOnEnter, goToField, removeFunctionFromObject, toNumberFormat } from "@utils/Utils";
import { formatCant, roundCant } from "@utils/Cant";
import { OpenAddProductPubSub } from "@utils/pubsubs/PubSub";
import ProductCodeTypeahead from "@components/dynamic/ProductCodeTypeahead";
import { getTenantId } from "@utils/Auth";
import { TypeaheadHandle } from "@components/dynamic/ProductCodeTypeahead/base";

type QuickAddProductFormProps = {
  onLoadedProduct?: (product: EDocumentProductDataType) => void,
  showCost?: boolean
}

type BarcodeLoadItem = {
  product?: ProductDataType,
  overridePrice: boolean
}

const QuickAddProductForm = (props: QuickAddProductFormProps) => {
  const { onLoadedProduct, showCost = false } = props
  const dispatch = useAppDispatch();
  const [quickAddProduct, setQuickAddProduct] = useState<QuickAddProductFormData>({} as QuickAddProductFormData);
  const [isProductLoaded, setIsProductLoaded] = useState<boolean>(false);
  const [productData, setProductData] = useState<ProductDataType>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const currency = useAppSelector((state) => state.currency);
  const priceCode = useAppSelector((state) => state.eDocument.priceCode);
  const priceData = useAppSelector((state) => state.eDocument.priceData);

  const typeaheadRef = useRef<TypeaheadHandle>(null);
  const codigoRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const cantRef = useRef<HTMLInputElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);

  const setFormData = (prop: keyof QuickAddProductFormData, val: any) => {
    const newPropData = {
      ...quickAddProduct,
      [prop]: val
    }
    setQuickAddProduct(newPropData)
  }

  const addProductToDocument = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      triggerAddProduct()
    }
  }

  const triggerAddProduct = (formData?: QuickAddProductFormData, product?: ProductDataType) => {
    const newQuickAddProduct = formData || quickAddProduct
    const fullProductData = product || productData
    if (!newQuickAddProduct.descripcion) {
      showAlert("danger", "la descripcion del producto es obligatoria");
      goToField(descriptionRef)
      return
    }

    if (!newQuickAddProduct.cant) {
      showAlert("danger", "La Cantidad de productos es obligatoria");
      goToField(cantRef)
      return
    }
    if (!newQuickAddProduct.price) {
      showAlert("danger", "El Precio del producto es obligatoria");
      goToField(priceRef)
      return
    }

    const cantidad = roundCant(Number(newQuickAddProduct.cant));
    if (isNaN(cantidad) || cantidad < 0) {
      showAlert("danger", "La cantidad de productos no es correcta");
      goToField(cantRef)
      return
    }

    const precio = Number(newQuickAddProduct.price);
    if (isNaN(precio) || precio < 0) {
      showAlert("danger", "El Precio del producto no es correcta");
      goToField(priceRef)
      return
    }

    const productToAdd: EDocumentProductDataType = {
      productId: fullProductData.id,
      exento: fullProductData.exento,
      tipo_producto: fullProductData.tipo_producto,
      description: newQuickAddProduct.descripcion,
      codigo: fullProductData.codigo,
      cant: cantidad,
      price: precio,
      subtotal: Number(toNumberFormat(precio * cantidad)),
      porcentaje_impuesto: TAXES_PERCENTAGES_VALUES[`${fullProductData.porcentaje_impuesto}`],
      codigo_tarifa: fullProductData.porcentaje_impuesto,
      tipo_impuesto: fullProductData.tipo_impuesto,
      unidad_medida: fullProductData.unidad_medida,
      comision: 0,
      codigo_cabys: fullProductData.codigo_cabys,
      porcentaje_descuento: 0,
      descuento: 0,
      impuestos: 0,
      impuestos_servicio: 0,
      product: fullProductData
    }

    if (newQuickAddProduct.cant) {
      dispatch(addProduct(removeFunctionFromObject(productToAdd)));
      if (onLoadedProduct) {
        onLoadedProduct(productToAdd)
      }

      const didAddSingle = cantidad === 1;
      showAlert("success", `Se ${didAddSingle ? 'agrego' : 'agregaron'} ${formatCant(cantidad)} ${didAddSingle ? 'unidad' : 'unidades'} del producto ${newQuickAddProduct.codigo}`, {}, 2000)
      clearSearchForm();

    }
  }

  const clickSearch = () => {
    if (quickAddProduct.codigo) {
      setIsLoading(true);
      searchProduct(quickAddProduct.codigo);
    } else {
      OpenAddProductPubSub.publish({ openOffCanvas: true })
    }
  }
  const loadProduct = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      setIsLoading(true);

      const input = e.target as HTMLInputElement;
      searchProduct(input.value);
    }
  }

  const clearSearchForm = () => {
    typeaheadRef.current?.clear()
    setIsProductLoaded(false)
    setProductData({})
    setQuickAddProduct({} as QuickAddProductFormData)
    setTimeout(() => {
      goToField(codigoRef)
    });

  }

  const loadByBarcode = async (productCode: string): Promise<BarcodeLoadItem | undefined> => {
    try {
      const parts = productCode.split(" ");
      if (parts.length == 2 && parts[0].startsWith("p")) {

        const tenantId = await getTenantId()
        let product = await ProductApi.get(`${tenantId}-${parts[0].substring(1)}`)
        if (!product?.id) {
          product = await ProductApi.getProductByCode(parts[0].substring(1))
        }
        return {
          product: {
            ...product,
            costo: Number(parts[1] || product.costo || 0)
          },
          overridePrice: true
        }
      }
      const codigoBarra = Number(productCode)
      if (!isNaN(codigoBarra)) {
        const product = await ProductApi.getProductByCodigoBarras(`${codigoBarra}`)
        return {
          product,
          overridePrice: false
        }
      }
    } catch (err) {
      console.log("no able to find product by barcode")
    }
    return undefined
  }

  const searchProduct = async (productCode: string) => {
    try {
      let product, overridePrice = false, fromBarcode = false;
      const loadProductData = await loadByBarcode(productCode)
      if (loadProductData) {
        product = loadProductData.product;
        overridePrice = loadProductData.overridePrice;
      }
      if (product?.id) {
        fromBarcode = true
      } else {
        product = await ProductApi.getProductByCode(productCode)
      }
      setProductData(product)
      setIsProductLoaded(true)

      let productPrice
      if (showCost || overridePrice) {
        productPrice = Number(product.costo || 0)
      } else {
        productPrice = calculateProductPrice(product, priceCode || priceData);
        productPrice = Number(productPrice)
      }
      const newProduct = {
        codigo: quickAddProduct.codigo,
        cant: 1,
        descripcion: `${product.descripcion_fact}`,
        price: productPrice
      }

      setQuickAddProduct(newProduct)
      setTimeout(() => {
        goToField(descriptionRef)
      });

      if (fromBarcode) {
        triggerAddProduct(newProduct, product)

      }
    } catch (err) {
      if (err instanceof NotFoundError) {
        showAlert("danger", err.message);
      }
      clearSearchForm()
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <Row className="g-3 align-items-center">
        <Col md="2">
          <FormGroup>
            <Label className="pe-3"
              for={"quick-add-code"}
            >
              Codigo
            </Label>
            <InputGroup>
              <ProductCodeTypeahead
                ref={typeaheadRef}
                inputRef={codigoRef}
                searchProduct={searchProduct}
                loadProduct={loadProduct}
                setFormData={(field, value) => setFormData(field as keyof QuickAddProductFormData, value)}
              />
              {/* <Input
                id="quick-add-code"
                type="text"
                innerRef={codigoRef}
                value={quickAddProduct.codigo || ""}
                onChange={(e) => setFormData("codigo", e.target.value)}
                onKeyDown={loadProduct}
              /> */}
              {!showCost && (<InputGroupText className="p-0">
                <Button type="button" onClick={clickSearch} className={styles.searchProduct} color="primary" size="sm"><FontAwesomeIcon icon={faMagnifyingGlass} /></Button>
              </InputGroupText>)}
            </InputGroup>
          </FormGroup>
        </Col>
        <Col md="5">
          <FormGroup>
            <Label className="pe-3"
              for={"quick-add-description"}
            >
              Description
            </Label>
            <InputGroup>
              <Input
                disabled={!isProductLoaded}
                innerRef={descriptionRef}
                id="quick-add-description"
                value={quickAddProduct.descripcion || ""}
                onChange={(e) => setFormData("descripcion", e.target.value)}
                onKeyDown={(e) => goNextOnEnter(e, cantRef)}
                type="text"
              />
            </InputGroup>
          </FormGroup>
        </Col>
        <Col md="2">
          <FormGroup>
            <Label className="pe-3"
              for={"quick-add-cant"}
            >
              Cantidad
              {productData.id && (<span className={styles.currentExistencies}>{formatCant(productData.existencias)}</span>)}
            </Label>
            <InputGroup>
              <Input
                disabled={!isProductLoaded}
                id="quick-add-cant"
                innerRef={cantRef}
                value={quickAddProduct.cant || ""}
                onChange={(e) => setFormData("cant", e.target.value)}
                onKeyDown={(e) => goNextOnEnter(e, priceRef)}
                type="text"
              />
            </InputGroup>
          </FormGroup>
        </Col>
        <Col md="2">
          <FormGroup>
            <Label className="pe-3"
              for={"quick-add-price"}
            >
              Precio
            </Label>
            <InputGroup>
              <InputGroupText>
                {currency.currencySymbol}
              </InputGroupText>
              <Input
                disabled={!isProductLoaded}
                id="quick-add-price"
                innerRef={priceRef}
                value={quickAddProduct.price || ""}
                onChange={(e) => setFormData("price", e.target.value)}
                onKeyDown={addProductToDocument}
                type="text"
              />
            </InputGroup>
          </FormGroup>
        </Col>
        <Col>
          <Button disabled={!isProductLoaded} className={styles.addButton} onClick={() => triggerAddProduct()} color="primary" type="button">
            Agregar
          </Button>
        </Col>
      </Row >
      <LoadingScreen isLoading={isLoading} />
    </>

  )
}

export default QuickAddProductForm;